package com.kanati.admin.features.platform

import androidx.compose.runtime.Composable
import androidx.compose.runtime.getValue
import androidx.compose.runtime.mutableStateOf
import androidx.compose.runtime.remember
import androidx.compose.runtime.rememberCoroutineScope
import androidx.compose.runtime.setValue
import com.kanati.admin.api.AdminApiClient
import com.kanati.admin.components.ActionButton
import com.kanati.admin.components.BannerTone
import com.kanati.admin.components.FieldRow
import com.kanati.admin.components.SectionCard
import com.kanati.admin.components.StatusBanner
import com.kanati.admin.components.TextField
import com.kanati.admin.state.MaterialThemePalette
import com.kanati.admin.state.SelectedDistrictContext
import com.kanati.admin.state.formatTaxRateInput
import com.kanati.admin.state.parseTaxRateInputToBps
import com.kanati.shared.admin.AccessScope
import com.kanati.shared.admin.CreateOperatorRequest
import com.kanati.shared.admin.OperatorAccessAssignmentRequest
import com.kanati.shared.district.CreateDistrictRequest
import com.kanati.shared.district.DEFAULT_DISTRICT_TAX_RATE_BPS
import com.kanati.shared.district.DEFAULT_DISTRICT_TIMEZONE
import com.kanati.shared.district.District
import com.kanati.shared.district.USState
import kotlinx.coroutines.launch
import org.jetbrains.compose.web.attributes.selected
import org.jetbrains.compose.web.css.DisplayStyle
import org.jetbrains.compose.web.css.FlexDirection
import org.jetbrains.compose.web.css.borderRadius
import org.jetbrains.compose.web.css.display
import org.jetbrains.compose.web.css.flexDirection
import org.jetbrains.compose.web.css.gap
import org.jetbrains.compose.web.css.margin
import org.jetbrains.compose.web.css.padding
import org.jetbrains.compose.web.css.px
import org.jetbrains.compose.web.dom.Div
import org.jetbrains.compose.web.dom.Option
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Select
import org.jetbrains.compose.web.dom.Text

@Composable
fun PlatformDashboardScreen(
    apiClient: AdminApiClient,
    districts: List<District>,
    selectedDistrict: SelectedDistrictContext?,
    districtLoading: Boolean,
    districtLoadError: String?,
    theme: MaterialThemePalette,
    onDistrictSelected: (String) -> Unit,
    onReloadContext: () -> Unit,
    onDistrictCreated: (District) -> Unit,
    onInitialAdminCreated: (String) -> Unit,
) {
    val scope = rememberCoroutineScope()
    var districtName by remember { mutableStateOf("") }
    var city by remember { mutableStateOf("") }
    var county by remember { mutableStateOf("") }
    var state by remember { mutableStateOf(USState.CO) }
    var timezone by remember { mutableStateOf(DEFAULT_DISTRICT_TIMEZONE) }
    val timezoneOptions = remember(state) { timezoneOptionsFor(state) }
    var taxRate by remember { mutableStateOf(formatTaxRateInput(DEFAULT_DISTRICT_TAX_RATE_BPS)) }
    var createDistrictError by remember { mutableStateOf<String?>(null) }
    var createDistrictSuccess by remember { mutableStateOf<String?>(null) }
    var isCreatingDistrict by remember { mutableStateOf(false) }
    var createdDistrict by remember { mutableStateOf<District?>(null) }

    var adminEmail by remember { mutableStateOf("") }
    var adminDisplayName by remember { mutableStateOf("") }
    var adminPassword by remember { mutableStateOf("") }
    var createAdminError by remember { mutableStateOf<String?>(null) }
    var createAdminSuccess by remember { mutableStateOf<String?>(null) }
    var isCreatingAdmin by remember { mutableStateOf(false) }

    fun resetInitialAdminForm() {
        adminEmail = ""
        adminDisplayName = ""
        adminPassword = ""
        createAdminError = null
        createAdminSuccess = null
    }

    SectionCard(
        title = "Platform Setup",
        theme = theme,
        description = "Create a district, then hand it off by creating the first district-wide admin without leaving the admin app.",
        headerActions = {
            ActionButton(
                label = if (districtLoading) "Loading..." else "Refresh",
                onClick = onReloadContext,
                theme = theme,
                isPrimary = false,
                isDisabled = districtLoading || isCreatingDistrict || isCreatingAdmin,
            )
        },
    ) {
        districtLoadError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        if (districts.isEmpty() && districtLoadError == null) {
            StatusBanner(
                message = "No districts are configured yet. Create the first district to unlock district-level setup flows.",
                theme = theme,
            )
        } else {
            val selectedDistrictMessage = selectedDistrict?.let {
                "Selected district: ${it.name}."
            } ?: "Select an existing district above or create a new one here."
            StatusBanner(
                message = "${districts.size} district${if (districts.size == 1) "" else "s"} available. $selectedDistrictMessage",
                theme = theme,
            )
        }

        createDistrictError?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Error,
            )
        }

        createDistrictSuccess?.let {
            StatusBanner(
                message = it,
                theme = theme,
                tone = BannerTone.Success,
            )
        }

        FieldRow {
            TextField(
                theme = theme,
                label = "District Name",
                value = districtName,
                placeholder = "Pilot District",
                onValueChange = {
                    districtName = it
                    createDistrictError = null
                },
            )
            TextField(
                theme = theme,
                label = "City",
                value = city,
                placeholder = "Denver",
                onValueChange = {
                    city = it
                    createDistrictError = null
                },
            )
        }

        FieldRow {
            TextField(
                theme = theme,
                label = "County",
                value = county,
                placeholder = "Denver",
                onValueChange = {
                    county = it
                    createDistrictError = null
                },
            )
            StateSelect(
                value = state,
                theme = theme,
                onValueChange = { nextState ->
                    val nextTimezoneOptions = timezoneOptionsFor(nextState)
                    state = nextState
                    if (nextTimezoneOptions.none { option -> option.id == timezone }) {
                        timezone = nextTimezoneOptions.first().id
                    }
                    createDistrictError = null
                },
            )
        }

        FieldRow {
            TimezoneSelect(
                theme = theme,
                value = timezone,
                options = timezoneOptions,
                onValueChange = {
                    timezone = it
                    createDistrictError = null
                },
            )
            TextField(
                theme = theme,
                label = "Default Sales Tax Rate",
                value = taxRate,
                placeholder = "7.25",
                onValueChange = {
                    taxRate = it
                    createDistrictError = null
                },
            )
        }

        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.92rem")
                    property("line-height", "1.5")
                }
            }
        ) {
            Text("Timezone choices are filtered by state and stored as IANA values for district-local reporting. Sales tax is entered as a percentage and stored in basis points.")
        }

        ActionButton(
            label = if (isCreatingDistrict) "Creating..." else "Create District",
            onClick = {
                val taxRateBps = parseTaxRateInputToBps(taxRate)
                when {
                    districtName.isBlank() -> createDistrictError = "District name is required."
                    city.isBlank() -> createDistrictError = "City is required."
                    county.isBlank() -> createDistrictError = "County is required."
                    timezone.isBlank() -> createDistrictError = "Timezone is required."
                    taxRateBps == null -> createDistrictError = "Enter a valid sales tax rate such as 7.25."
                    else -> scope.launch {
                        isCreatingDistrict = true
                        createDistrictError = null
                        createDistrictSuccess = null
                        try {
                            val district = apiClient.createDistrict(
                                CreateDistrictRequest(
                                    name = districtName.trim(),
                                    city = city.trim(),
                                    county = county.trim(),
                                    state = state,
                                    timezone = timezone.trim(),
                                    defaultTaxRateBps = taxRateBps,
                                )
                            )
                            createdDistrict = district
                            createDistrictSuccess = "${district.name} was created. Continue below to create the first district admin."
                            resetInitialAdminForm()
                            onDistrictCreated(district)
                        } catch (exception: Exception) {
                            createDistrictError = exception.message ?: "Unable to create district."
                        } finally {
                            isCreatingDistrict = false
                        }
                    }
                }
            },
            theme = theme,
            isDisabled = isCreatingDistrict || isCreatingAdmin,
        )
    }

    createdDistrict?.let { district ->
        SectionCard(
            title = "Create Initial District Admin",
            theme = theme,
            description = "This user will be created with district-level, district-wide admin access for ${district.name}.",
        ) {
            createAdminError?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Error,
                )
            }

            createAdminSuccess?.let {
                StatusBanner(
                    message = it,
                    theme = theme,
                    tone = BannerTone.Success,
                )
            }

            StatusBanner(
                message = "Assignment: district-level, district-wide, admin access.",
                theme = theme,
            )

            FieldRow {
                TextField(
                    theme = theme,
                    label = "Email",
                    value = adminEmail,
                    placeholder = "admin@district.org",
                    onValueChange = {
                        adminEmail = it
                        createAdminError = null
                    },
                )
                TextField(
                    theme = theme,
                    label = "Display Name",
                    value = adminDisplayName,
                    placeholder = "District Admin",
                    onValueChange = {
                        adminDisplayName = it
                        createAdminError = null
                    },
                )
            }

            TextField(
                theme = theme,
                label = "Temporary Password",
                value = adminPassword,
                placeholder = "Temporary password",
                onValueChange = {
                    adminPassword = it
                    createAdminError = null
                },
            )

            Div(
                attrs = {
                    style {
                        display(DisplayStyle.Flex)
                        flexDirection(FlexDirection.Row)
                        gap(10.px)
                        property("flex-wrap", "wrap")
                    }
                }
            ) {
                ActionButton(
                    label = if (isCreatingAdmin) "Creating..." else "Create Initial District Admin",
                    onClick = {
                        when {
                            adminEmail.isBlank() -> createAdminError = "Email is required."
                            adminDisplayName.isBlank() -> createAdminError = "Display name is required."
                            adminPassword.isBlank() -> createAdminError = "Password is required."
                            else -> scope.launch {
                                isCreatingAdmin = true
                                createAdminError = null
                                createAdminSuccess = null
                                try {
                                    apiClient.createUser(
                                        CreateOperatorRequest(
                                            districtId = district.id,
                                            email = adminEmail.trim(),
                                            displayName = adminDisplayName.trim(),
                                            password = adminPassword,
                                            assignment = OperatorAccessAssignmentRequest(
                                                scope = AccessScope.DISTRICT,
                                                adminAccess = true,
                                                posAccess = false,
                                                districtWide = true,
                                            ),
                                        )
                                    )
                                    createAdminSuccess = "Initial district admin created for ${district.name}."
                                    onInitialAdminCreated(district.id)
                                } catch (exception: Exception) {
                                    createAdminError = exception.message ?: "Unable to create the initial district admin."
                                } finally {
                                    isCreatingAdmin = false
                                }
                            }
                        }
                    },
                    theme = theme,
                    isDisabled = isCreatingDistrict || isCreatingAdmin,
                )

                ActionButton(
                    label = "Select District",
                    onClick = { onDistrictSelected(district.id) },
                    theme = theme,
                    isPrimary = false,
                    isDisabled = isCreatingDistrict || isCreatingAdmin,
                )
            }
        }
    }
}

private data class TimezoneOption(
    val id: String,
    val label: String,
)

private val EASTERN_TIME = timezoneOption("America/New_York", "Eastern Time")
private val CENTRAL_TIME = timezoneOption("America/Chicago", "Central Time")
private val MOUNTAIN_TIME = timezoneOption("America/Denver", "Mountain Time")
private val PACIFIC_TIME = timezoneOption("America/Los_Angeles", "Pacific Time")
private val HAWAII_TIME = timezoneOption("Pacific/Honolulu", "Hawaii Time")
private val ALASKA_TIME = timezoneOption("America/Anchorage", "Alaska Time")
private val ALEUTIAN_TIME = timezoneOption("America/Adak", "Hawaii-Aleutian Time")
private val ARIZONA_TIME = timezoneOption("America/Phoenix", "Arizona Time (no daylight saving)")
private val IDAHO_MOUNTAIN_TIME = timezoneOption("America/Boise", "Mountain Time (southern Idaho)")
private val INDIANA_EASTERN_TIME = timezoneOption("America/Indiana/Indianapolis", "Eastern Time (Indiana)")
private val KENTUCKY_EASTERN_TIME = timezoneOption("America/Kentucky/Louisville", "Eastern Time (Kentucky)")
private val MICHIGAN_CENTRAL_TIME = timezoneOption("America/Menominee", "Central Time (Upper Peninsula)")
private val ALASKA_JUNEAU_TIME = timezoneOption("America/Juneau", "Alaska Time (Juneau)")
private val ALASKA_METLAKATLA_TIME = timezoneOption("America/Metlakatla", "Alaska Time (Metlakatla)")
private val ALASKA_NOME_TIME = timezoneOption("America/Nome", "Alaska Time (Nome)")
private val ALASKA_SITKA_TIME = timezoneOption("America/Sitka", "Alaska Time (Sitka)")
private val ALASKA_YAKUTAT_TIME = timezoneOption("America/Yakutat", "Alaska Time (Yakutat)")

private val timezoneOptionsByState: Map<USState, List<TimezoneOption>> = mapOf(
    USState.AK to listOf(
        ALASKA_TIME,
        ALASKA_JUNEAU_TIME,
        ALASKA_METLAKATLA_TIME,
        ALASKA_NOME_TIME,
        ALASKA_SITKA_TIME,
        ALASKA_YAKUTAT_TIME,
        ALEUTIAN_TIME,
    ),
    USState.AL to listOf(CENTRAL_TIME),
    USState.AR to listOf(CENTRAL_TIME),
    USState.AZ to listOf(ARIZONA_TIME, MOUNTAIN_TIME),
    USState.CA to listOf(PACIFIC_TIME),
    USState.CO to listOf(MOUNTAIN_TIME),
    USState.CT to listOf(EASTERN_TIME),
    USState.DE to listOf(EASTERN_TIME),
    USState.FL to listOf(EASTERN_TIME, CENTRAL_TIME),
    USState.GA to listOf(EASTERN_TIME),
    USState.HI to listOf(HAWAII_TIME),
    USState.IA to listOf(CENTRAL_TIME),
    USState.ID to listOf(IDAHO_MOUNTAIN_TIME, PACIFIC_TIME),
    USState.IL to listOf(CENTRAL_TIME),
    USState.IN to listOf(INDIANA_EASTERN_TIME, CENTRAL_TIME),
    USState.KS to listOf(CENTRAL_TIME, MOUNTAIN_TIME),
    USState.KY to listOf(KENTUCKY_EASTERN_TIME, CENTRAL_TIME),
    USState.LA to listOf(CENTRAL_TIME),
    USState.MA to listOf(EASTERN_TIME),
    USState.MD to listOf(EASTERN_TIME),
    USState.ME to listOf(EASTERN_TIME),
    USState.MI to listOf(timezoneOption("America/Detroit", "Eastern Time (Michigan)"), MICHIGAN_CENTRAL_TIME),
    USState.MN to listOf(CENTRAL_TIME),
    USState.MO to listOf(CENTRAL_TIME),
    USState.MS to listOf(CENTRAL_TIME),
    USState.MT to listOf(MOUNTAIN_TIME),
    USState.NC to listOf(EASTERN_TIME),
    USState.ND to listOf(CENTRAL_TIME, MOUNTAIN_TIME),
    USState.NE to listOf(CENTRAL_TIME, MOUNTAIN_TIME),
    USState.NH to listOf(EASTERN_TIME),
    USState.NJ to listOf(EASTERN_TIME),
    USState.NM to listOf(MOUNTAIN_TIME),
    USState.NV to listOf(PACIFIC_TIME),
    USState.NY to listOf(EASTERN_TIME),
    USState.OH to listOf(EASTERN_TIME),
    USState.OK to listOf(CENTRAL_TIME),
    USState.OR to listOf(PACIFIC_TIME, MOUNTAIN_TIME),
    USState.PA to listOf(EASTERN_TIME),
    USState.RI to listOf(EASTERN_TIME),
    USState.SC to listOf(EASTERN_TIME),
    USState.SD to listOf(CENTRAL_TIME, MOUNTAIN_TIME),
    USState.TN to listOf(CENTRAL_TIME, EASTERN_TIME),
    USState.TX to listOf(CENTRAL_TIME, MOUNTAIN_TIME),
    USState.UT to listOf(MOUNTAIN_TIME),
    USState.VA to listOf(EASTERN_TIME),
    USState.VT to listOf(EASTERN_TIME),
    USState.WA to listOf(PACIFIC_TIME),
    USState.WI to listOf(CENTRAL_TIME),
    USState.WV to listOf(EASTERN_TIME),
    USState.WY to listOf(MOUNTAIN_TIME),
)

private fun timezoneOptionsFor(state: USState): List<TimezoneOption> =
    timezoneOptionsByState[state] ?: listOf(timezoneOption(DEFAULT_DISTRICT_TIMEZONE, "District Timezone"))

private fun timezoneOption(id: String, label: String): TimezoneOption =
    TimezoneOption(
        id = id,
        label = "$label ($id)",
    )

@Composable
private fun StateSelect(
    value: USState,
    theme: MaterialThemePalette,
    onValueChange: (USState) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                property("flex", "1")
                property("min-width", "0")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text("State")
        }

        Select(
            attrs = {
                onChange { event ->
                    event.value?.let { selectedValue ->
                        runCatching { USState.valueOf(selectedValue) }
                            .getOrNull()
                            ?.let(onValueChange)
                    }
                }
                style {
                    padding(14.px, 16.px)
                    borderRadius(14.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("background-color", theme.surface)
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    property("font-size", "0.98rem")
                    property("font-weight", "500")
                    property("letter-spacing", "-0.01em")
                }
            }
        ) {
            USState.values().forEach { state ->
                Option(
                    value = state.name,
                    attrs = {
                        if (state == value) selected()
                    },
                ) {
                    Text(state.name)
                }
            }
        }
    }
}

@Composable
private fun TimezoneSelect(
    value: String,
    options: List<TimezoneOption>,
    theme: MaterialThemePalette,
    onValueChange: (String) -> Unit,
) {
    Div(
        attrs = {
            style {
                display(DisplayStyle.Flex)
                flexDirection(FlexDirection.Column)
                gap(6.px)
                property("flex", "1")
                property("min-width", "0")
            }
        }
    ) {
        P(
            attrs = {
                style {
                    margin(0.px)
                    property("color", theme.onSurfaceVariant)
                    property("font-size", "0.78rem")
                    property("font-weight", "700")
                    property("letter-spacing", "0.06em")
                    property("text-transform", "uppercase")
                }
            }
        ) {
            Text("Timezone")
        }

        Select(
            attrs = {
                onChange { event ->
                    event.value?.let(onValueChange)
                }
                style {
                    padding(14.px, 16.px)
                    borderRadius(14.px)
                    property("box-sizing", "border-box")
                    property("width", "100%")
                    property("background-color", theme.surface)
                    property("color", theme.onSurface)
                    property("border", "1px solid ${theme.outline}")
                    property("box-shadow", "inset 0 1px 0 ${theme.scrim}")
                    property("font-size", "0.98rem")
                    property("font-weight", "500")
                    property("letter-spacing", "-0.01em")
                }
            }
        ) {
            options.forEach { option ->
                Option(
                    value = option.id,
                    attrs = {
                        if (option.id == value) selected()
                    },
                ) {
                    Text(option.label)
                }
            }
        }
    }
}
